import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkOutlined, MinusOutlined } from '@ant-design/icons'
import { Button, Col, Row } from 'antd'
import { isNil, map, noop } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'
import { areSameValue, uniquePrimitives } from '../../../core/utils'

import { PAGINATION } from '../../../common/constants'
import { findNodeByFilter } from '../../../common/utils'

import {
  setMenuTitleExpandedRowKeys,
  setMenuTitleHighlightedRowKey,
} from '../../../menu-title/redux/actions'
import {
  LABEL_LEGEND_CONFLICT_RESOLUTION_TYPE,
  LABEL_LEGEND_MATCH_TYPE,
  MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE,
} from '../../constants'
import { labelLegendPropType } from '../../prop-types'
import {
  StyledLabelLegendsInput,
  StyledLabelLegendsMatchTypeContainer,
  StyledLabelLegendsSelect,
} from '../styled'

const LabelLegendRow = ({
  index,
  labelLegend,
  options,
  onChange,
  onDelete,
  isDisabled,
  type,
}) => {
  const dispatch = useDispatch()
  const idFieldName = useMemo(() => `${type}Id`, [type])

  const {
    [idFieldName]: legendId,
    label,
    isSuggestedByAi,
    conflictResolutionType,
    matchType,
    isChoice,
    originDishId,
    originMenuTitleId,
  } = labelLegend

  const { setQueryParams } = useQueryParams()

  const { data: menuCurationTask, menuTitleExpandedRowKeys: expandedRowKeys } =
    useSelector(store => store.menuCurationTasks.menuCurationTask)

  const { menuTitles } = menuCurationTask?.menu || {}

  const isFromUnknownWords = useMemo(
    () =>
      conflictResolutionType ===
      LABEL_LEGEND_CONFLICT_RESOLUTION_TYPE.UNKNOWN_WORDS,
    [conflictResolutionType],
  )

  const originLink = useMemo(() => {
    if (!isNil(originDishId)) {
      return {
        idToOpen: originDishId,
        type: MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.DISH,
      }
    }

    if (!isNil(originMenuTitleId)) {
      return {
        idToOpen: originMenuTitleId,
        type: MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.MENU_TITLE,
      }
    }

    return null
  }, [originDishId, originMenuTitleId])

  const handleHighlightMenuTitle = useCallback(() => {
    const menuTitleToOpen = findNodeByFilter(menuTitles, ({ id }) =>
      areSameValue(id, originLink?.idToOpen),
    )

    if (!isNil(menuTitleToOpen)) {
      dispatch(
        setMenuTitleExpandedRowKeys(
          uniquePrimitives([
            ...(expandedRowKeys || []),
            ...map(
              [...menuTitleToOpen.idPath, menuTitleToOpen.id],
              menuTitleId => `menuTitle-${menuTitleId}`,
            ),
          ]),
        ),
      )
      dispatch(setMenuTitleHighlightedRowKey(`menuTitle-${menuTitleToOpen.id}`))
    }
  }, [dispatch, expandedRowKeys, menuTitles, originLink?.idToOpen])

  const handleHighlightDish = useCallback(() => {
    if (!isNil(originLink)) {
      setQueryParams({
        dishIds: [originLink.idToOpen],
        shouldScrollToDish: true,
        currentPage: PAGINATION.DEFAULT_PAGE,
      })
    }
  }, [originLink, setQueryParams])

  const highlightLinkButton = useMemo(() => {
    if (isNil(originLink)) {
      return null
    }

    return (
      <Col span={2}>
        {originLink.type === MENU_CURATION_TASK_ENTITY_LINK_ORIGIN_TYPE.DISH ? (
          <Button
            icon={<LinkOutlined />}
            shape="circle"
            onClick={handleHighlightDish}
          />
        ) : (
          <Button
            icon={<LinkOutlined />}
            shape="circle"
            onClick={handleHighlightMenuTitle}
          />
        )}
      </Col>
    )
  }, [handleHighlightDish, handleHighlightMenuTitle, originLink])

  const deleteLabelLegend = useCallback(
    () => onDelete(index),
    [index, onDelete],
  )

  const handleSelectChange = useCallback(
    value => {
      onChange(index, { ...labelLegend, [idFieldName]: value })
    },
    [index, labelLegend, idFieldName, onChange],
  )

  const handleLabelChange = useCallback(
    e => {
      onChange(index, { ...labelLegend, label: e.target.value })
    },
    [index, labelLegend, onChange],
  )

  return (
    <Row gutter={[8, 6]}>
      <StyledLabelLegendsMatchTypeContainer span={2}>
        {LABEL_LEGEND_MATCH_TYPE[matchType] || ''}
      </StyledLabelLegendsMatchTypeContainer>
      <Col span={8}>
        <StyledLabelLegendsInput
          disabled={isDisabled}
          value={label}
          isSuggestedByAi={isSuggestedByAi}
          isFromUnknownWords={isFromUnknownWords}
          isChoice={isChoice}
          placeholder="Label"
          onChange={handleLabelChange}
        />
      </Col>
      <Col span={10}>
        <StyledLabelLegendsSelect
          disabled={isDisabled}
          value={legendId}
          isSuggestedByAi={isSuggestedByAi}
          isFromUnknownWords={isFromUnknownWords}
          isChoice={isChoice}
          onChange={handleSelectChange}
          options={options}
        />
      </Col>
      {!isDisabled && (
        <Col span={2}>
          <Button
            icon={<MinusOutlined />}
            shape="circle"
            onClick={deleteLabelLegend}
          />
        </Col>
      )}
      {highlightLinkButton}
    </Row>
  )
}

LabelLegendRow.propTypes = {
  index: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  labelLegend: labelLegendPropType.isRequired,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  isDisabled: PropTypes.bool,
}

LabelLegendRow.defaultProps = {
  onChange: noop,
  onDelete: noop,
  isDisabled: false,
}

export default LabelLegendRow
